/**
 * The camera behind the scan bar.
 *
 * ui-design-system.md 10.1 is the binding spec: a live preview streaming the
 * moment a scanning screen opens (the Telegram attachment-panel reference), codes
 * decoded continuously off the video with no tap, the catalogue photo taken from
 * that same stream, and the camera dark whenever no scanning screen is mounted -
 * these are the staff's own phones, so battery and privacy are the operator's,
 * not ours.
 *
 * Everything the browser supplies is a port, for the same reason scanFocus talks
 * to a FocusTarget rather than an input element: a controller that reaches for
 * navigator.mediaDevices cannot be tested without a camera, a permission prompt
 * and a human. Here mediaDevices, the barcode detector, the frame pump and the
 * still-capture are all injected, and the React hook is the only place that knows
 * about the real ones.
 *
 * Decoded codes do not get their own pipeline. They go to onDecode, which the
 * hook wires straight into ScanFocusController.decoded(), so a camera read and a
 * wireless HID read are the same event by the time anything downstream sees it.
 * The HID scanner remains primary at the catalogue station; this is an addition.
 */

import type { CameraState } from '../ui/ScanBar'

export type { CameraState }

export interface CameraTrack {
  stop(): void
}

export interface CameraStream {
  getTracks(): CameraTrack[]
}

export interface MediaDevicesPort {
  getUserMedia(constraints: unknown): Promise<CameraStream>
}

/** The narrow slice of an HTMLVideoElement this controller actually touches. */
export interface VideoSurface {
  srcObject: unknown
  readyState?: number
  videoWidth?: number
  videoHeight?: number
}

export interface DetectedCode {
  rawValue: string
}

export interface BarcodeDetectorPort {
  detect(source: unknown): Promise<DetectedCode[]>
}

/** Cancels a scheduled frame, so a stopped camera leaves no callback in flight. */
export type CancelFrame = () => void

export interface CameraPorts {
  /** Null where the browser exposes no mediaDevices at all - desktop, or an insecure origin. */
  mediaDevices?: MediaDevicesPort | null
  /**
   * Null where BarcodeDetector is missing (Safari, Firefox, older WebViews). That
   * is the documented fallback: the preview still streams and still takes the
   * catalogue photo, decode simply does not happen in software and the operator
   * uses the HID scanner or the text field. No dead UI either way.
   */
  createDetector?: (() => BarcodeDetectorPort) | null
  /** Frame pump. Defaults to requestAnimationFrame in the hook; stepped by hand in tests. */
  schedule?: (fn: () => void) => CancelFrame | void
  /** Grabs a still off the live preview. Supplied by the hook, which owns the canvas. */
  capture?: (video: VideoSurface) => Promise<Blob | null>
  onDecode?: (code: string) => void
  onChange?: (state: CameraState) => void
}

/** Rear camera, and a resolution high enough for a barcode to survive downscaling. */
export const REAR_CAMERA_CONSTRAINTS = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
}

/**
 * A refusal is not a failure - denied and unavailable are different words on the
 * screen and different recoveries (grant it in browser settings vs. use the
 * scanner), so they stay different states.
 */
function stateForError(err: unknown): CameraState {
  const name = (err as { name?: string } | null)?.name ?? ''
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') return 'denied'
  return 'unavailable'
}

export class CameraController {
  private current: CameraState = 'off'
  private video: VideoSurface | null = null
  private stream: CameraStream | null = null
  private detector: BarcodeDetectorPort | null = null
  private cancelFrame: CancelFrame | null = null
  /** Held so a code sitting in frame at 30fps fires once, not thirty times a second. */
  private lastCode: string | null = null
  private generation = 0

  constructor(private readonly ports: CameraPorts = {}) {}

  state(): CameraState {
    return this.current
  }

  /** False where the browser has no BarcodeDetector - the screen can say so honestly. */
  decodesInSoftware(): boolean {
    return this.detector !== null
  }

  attach(video: VideoSurface | null): void {
    this.video = video
    if (video && this.stream) video.srcObject = this.stream
  }

  async start(): Promise<CameraState> {
    if (this.current === 'starting' || this.current === 'streaming') return this.current
    const generation = ++this.generation
    this.set('starting')

    const devices = this.ports.mediaDevices
    if (!devices) return this.set('unavailable')

    let stream: CameraStream
    try {
      stream = await devices.getUserMedia(REAR_CAMERA_CONSTRAINTS)
    } catch (err) {
      return this.set(stateForError(err))
    }

    // The screen can close while the permission prompt is up. Whoever wins, the
    // stream that arrives late gets stopped rather than left running unseen.
    if (generation !== this.generation) {
      stopTracks(stream)
      return this.current
    }

    this.stream = stream
    if (this.video) this.video.srcObject = stream
    this.detector = this.makeDetector()
    const state = this.set('streaming')
    this.pump()
    return state
  }

  /** Idempotent, and the only thing that has to be right on unmount. */
  stop(): void {
    this.generation++
    this.cancelFrame?.()
    this.cancelFrame = null
    if (this.stream) stopTracks(this.stream)
    this.stream = null
    this.detector = null
    this.lastCode = null
    if (this.video) this.video.srcObject = null
    if (this.current !== 'off') this.set('off')
  }

  /** Unmount: stop the tracks and forget the element. */
  detach(): void {
    this.stop()
    this.video = null
  }

  async capturePhoto(): Promise<Blob | null> {
    if (this.current !== 'streaming' || !this.video || !this.ports.capture) return null
    try {
      return await this.ports.capture(this.video)
    } catch {
      return null
    }
  }

  private makeDetector(): BarcodeDetectorPort | null {
    const create = this.ports.createDetector
    if (!create) return null
    try {
      return create()
    } catch {
      // An unsupported format list throws on construction. That is a missing
      // decoder, not a broken camera - the preview stays up.
      return null
    }
  }

  private pump(): void {
    if (!this.detector) return
    const generation = this.generation
    const schedule = this.ports.schedule
    if (!schedule) return
    const cancel = schedule(() => {
      this.cancelFrame = null
      void this.readFrame(generation)
    })
    this.cancelFrame = typeof cancel === 'function' ? cancel : null
  }

  private async readFrame(generation: number): Promise<void> {
    if (generation !== this.generation || this.current !== 'streaming' || !this.detector || !this.video) return
    try {
      const found = await this.detector.detect(this.video)
      if (generation !== this.generation) return
      const raw = found.find((c) => c.rawValue)?.rawValue
      if (raw && raw !== this.lastCode) {
        this.lastCode = raw
        this.ports.onDecode?.(raw)
      } else if (!raw) {
        // The code left the frame, so the next sighting of it is a fresh scan.
        this.lastCode = null
      }
    } catch {
      // A dropped frame is not an error worth surfacing; the next one is 33ms away.
    }
    if (generation === this.generation) this.pump()
  }

  private set(next: CameraState): CameraState {
    if (this.current === next) return next
    this.current = next
    this.ports.onChange?.(next)
    return next
  }
}

function stopTracks(stream: CameraStream): void {
  for (const track of stream.getTracks()) {
    try {
      track.stop()
    } catch {
      // A track already ended by the OS throws on some Androids. Nothing to do.
    }
  }
}
